import { Component, inject, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { BatchCreateCampaignsService } from 'app/campaign/batch-create-campaigns.service';
import { OnsiteCampaign, OnsiteCampaignType } from 'app/campaign/campaign.model';
import { WidgetPreviewService } from 'app/campaign/campaigns/widget-preview.service';
import { Logger, LoggerFactory } from 'app/core/logger.service';
import { Subscription } from 'rxjs';
import { CrossAccountNavigationService } from 'app/shared/services/cross-account-navigation.service';

@Component({
  selector: 'app-tumbnail-browser',
  templateUrl: './tumbnail-browser.component.html',
  styleUrl: './tumbnail-browser.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TumbnailBrowserComponent implements OnInit, OnDestroy {
  private readonly logger: Logger;
  @Input() isDarkTheme;
  @Input() isMobilePreview: boolean = false;
  @Input() campaign: OnsiteCampaign;
  @Input()
  set campaignImg(value: { imageUrl: string } | undefined) {
    this._campaignImg = value;
  }
  get campaignImg() {
    return this._campaignImg;
  }
  @Input() campaignImagesMap;
  @Input() typeThumbnail: string;
  @Input() isGenerating: boolean;
  // The campaign's own account. Set by CampaignCardComponent so this link can guard against navigating in place to a campaign on another account.
  @Input() siteId?: number;
  @Input() siteName?: string;
  public subscriptions: Subscription[] = [];
  public OnsiteCampaignType = OnsiteCampaignType;
  private widgetPreviewService = inject(WidgetPreviewService);
  public crossAccountNavService = inject(CrossAccountNavigationService);
  private editedWidgets$ = this.widgetPreviewService.editedWidgets$;
  isUpdating = false;
  private _campaignImg?: { imageUrl: string };
  public isAbTestCampaign = false;

  constructor(
    loggerFactory: LoggerFactory,
    private readonly batchCreateCampaignsService: BatchCreateCampaignsService,
  ) {
    this.logger = loggerFactory.get('TumbnailBrowserComponent');
  }

  ngOnInit(): void {
    if (!this.batchCreateCampaignsService.isVirtualCampaign(this.campaign)) {
      this.subscriptions.push(
        this.batchCreateCampaignsService.campaignCreationCompletedBS.subscribe((createdCampaign) => {
          if (createdCampaign) {
            if (this.campaign.id === createdCampaign.id) {
              this.logger.debug('Removing loading indicator', createdCampaign);
              this.isGenerating = false; // Remove loading indicator
            }
          }
        }),
      );
    }
    this.subscriptions.push(
      this.editedWidgets$.subscribe((editedWidgets) => {
        const campaignWidgets = this.campaign.widgets ?? [];
        editedWidgets.forEach((editedWidget) => {
          if (this.isUpdating) return;
          if (campaignWidgets.some((cw) => cw.id === editedWidget.widgetId)) {
            this.isUpdating = true;
            this.widgetPreviewService
              .checkForUpdatedPreviewImg$(editedWidget.widgetId)
              .subscribe((updatedPreviewImg) => {
                if (updatedPreviewImg) {
                  this.campaignImg!.imageUrl = updatedPreviewImg;
                }
                this.isUpdating = false;
              });
          }
        });
      }),
    );
    this.isAbTestCampaign = this.isAbTest(this.campaign);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public get showThumbnail(): boolean {
    return (
      (this.typeThumbnail === TypeThumbnail.GridCampaign &&
        this.campaignImagesMap &&
        this.campaignImagesMap[this.campaign.id] &&
        this.campaignImagesMap[this.campaign.id].length > 0) ||
      (this.typeThumbnail === TypeThumbnail.PerformancePage && !!this.campaignImg)
    );
  }

  public isAbTest(campaign): boolean {
    return campaign.type === OnsiteCampaignType.ABTesting;
  }

  public get settingsLink(): string {
    return `${this.isAbTestCampaign ? '/campaigns/ab-tests' : '/campaigns'}/${this.campaign.id}/settings`;
  }
}

export enum TypeThumbnail {
  GridCampaign = 'grid-campaign',
  PerformancePage = 'performance-page',
}
